import { useRef, useState } from "react";
import { downscaleImage } from "@/utils/image";
import { docSizeError } from "@/utils/docFile";
import { isRetryableScanError } from "./applyScan";

export type ScanKind = "info" | "success" | "error";

export interface ScanOutcome {
  kind: ScanKind;
  msg: string;
}

export function useDocScan({
  reading,
  failed,
  run,
  onFile,
  onBusyChange,
  onPick,
  sizeError = docSizeError,
}: {
  reading: string;
  failed: string;
  run: (file: File, onlyEmpty: boolean) => Promise<ScanOutcome>;
  onFile: (f: File | null) => void;
  onBusyChange?: (busy: boolean) => void;
  onPick?: () => void;
  sizeError?: (file: File) => string | null;
}) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [kind, setKind] = useState<ScanKind>("info");
  const [retryFile, setRetryFile] = useState<File | null>(null);
  const onlyEmpty = useRef(false);

  function markBusy(next: boolean) {
    setBusy(next);
    onBusyChange?.(next);
  }

  async function read(file: File) {
    markBusy(true);
    setRetryFile(null);
    setKind("info");
    setMsg(reading);
    try {
      const out = await run(file, onlyEmpty.current);
      setKind(out.kind);
      setMsg(out.msg);
    } catch (e) {
      setKind("error");
      setMsg(failed);
      if (isRetryableScanError(e)) setRetryFile(file);
    } finally {
      markBusy(false);
    }
  }

  async function pick(file: File, reattaching: boolean) {
    onPick?.();
    const tooLarge = sizeError(file);
    if (tooLarge) {
      setRetryFile(null);
      setKind("error");
      setMsg(tooLarge);
      return;
    }
    onlyEmpty.current = reattaching;
    markBusy(true);
    setKind("info");
    setMsg(reading);
    const prepared = await downscaleImage(file);
    onFile(prepared);
    await read(prepared);
  }

  function clear() {
    onFile(null);
    setRetryFile(null);
    setMsg(null);
    setKind("info");
  }

  return {
    busy,
    msg,
    kind,
    pick,
    clear,
    retry: retryFile ? () => { void read(retryFile); } : undefined,
  };
}
